/**
 * MapPage — MAP (Main Access Panel) founder-board mockup.
 *
 * PREVIEW/MOCKUP PHASE. Owner picked a mix of Editorial (D2) and Blueprint (D3);
 * that merged direction is the primary render (?d=mix or ?d unset).
 * D2 and D3 in their pure forms are kept as toggleable reference views so
 * Owner can sanity-check the mix against its sources.
 *
 * Language rule (Owner-locked): 100 percent founder / layman voice on all
 * user-visible content. Zero engineer-speak in card titles or summaries.
 * Internal reference codes are kept only inside the detail drawer for now.
 *
 * URL: /map  (new; the old /board Vision Board will be retired after
 * Owner confirms this direction and MAP goes live with real data).
 *
 * Design contract: /app/design_guidelines.md (three directions specified in full).
 */

import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  ArrowLeft,
  RefreshCw,
  Pin,
  EyeOff,
  MessageSquare,
  Presentation,
  ExternalLink,
  Clock,
} from "lucide-react";
import { Button } from "../components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "../components/ui/sheet";
import {
  ToggleGroup,
  ToggleGroupItem,
} from "../components/ui/toggle-group";
import "./MapPage.css";

/* ─────────────────────────────────────────────────────────────
 * Seed data — plain founder / layman language.
 * Internal reference codes (C119 etc) hidden from card body;
 * only surfaced in the detail drawer as small metadata.
 * ────────────────────────────────────────────────────────── */

const MOCK_LANES = [
  {
    id: "shipped",
    title: "Just shipped",
    coord: "A · 01",
    intent: "The last thirty days",
    cards: [
      {
        id: "s1",
        title: "Noticeboard is back on its feet",
        summary:
          "Recovered our first live product from a rough week. Web address restored, permissions fixed, safety nets tightened. No customer impact.",
        app: "NB",
        ref: "C119·C120·C124·C127",
        timestamp: "this week",
        pinned: true,
      },
      {
        id: "s2",
        title: "Written down how to launch future products cleanly",
        summary:
          "Every mistake from the recovery is now in one checklist. The next product launch has a manual instead of a hope.",
        app: "MP",
        ref: "C130",
        timestamp: "today",
        pinned: false,
      },
      {
        id: "s3",
        title: "Small housekeeping wins in the background",
        summary:
          "Monitoring, alarms, and settings checks quietly tightened up across the cloud. Nothing shiny; makes everything else safer.",
        app: "MP",
        ref: "misc",
        timestamp: "this week",
        pinned: false,
      },
    ],
  },
  {
    id: "inflight",
    title: "In flight",
    coord: "A · 02",
    intent: "Being worked on right now",
    cards: [
      {
        id: "i1",
        title: "Building MAP — this founder view",
        summary:
          "A single view of the whole business — replaces the old Vision Board. What you're looking at right now.",
        app: "MP",
        ref: "C130+",
        timestamp: "now",
        pinned: true,
      },
      {
        id: "i2",
        title: "Next wave of products getting ready to ship together",
        summary:
          "Elevator IoT, TOCBox, and Estimator are all being prepared in parallel. A combined launch window is coming.",
        app: "EI",
        ref: "wave-2",
        timestamp: "ongoing",
        pinned: false,
      },
      {
        id: "i3",
        title: "Writing up the recovery lessons for the team",
        summary:
          "Three focused write-ups documenting what went wrong and how we caught it. So the next person on-call has a manual.",
        app: "MP",
        ref: "C120·C121·C123",
        timestamp: "queued",
        pinned: false,
      },
    ],
  },
  {
    id: "next",
    title: "Coming next",
    coord: "B · 01",
    intent: "Planned, not started",
    cards: [
      {
        id: "n1",
        title: "First customer launch of Elevator IoT (with TOCBox)",
        summary:
          "Our second real product goes live to customers. Uses the new launch playbook end-to-end. Hardware and software ship together.",
        app: "EI",
        ref: "queue-1",
        timestamp: "next",
        pinned: false,
      },
      {
        id: "n2",
        title: "Estimator opens to first quoting customers",
        summary:
          "Sales engineers go live with the tool. Onboarding materials and the pricing library land alongside.",
        app: "EC",
        ref: "queue-2",
        timestamp: "queued",
        pinned: false,
      },
      {
        id: "n3",
        title: "Cloud housekeeping — cost sanity + silent-outage sweep",
        summary:
          "Check the cloud bill, set spending alarms, and sweep for any latent outage risks like the one Noticeboard just had.",
        app: "MP",
        ref: "C99·C124-fu",
        timestamp: "queued",
        pinned: false,
      },
    ],
  },
  {
    id: "ideas",
    title: "Ideas & backlog",
    coord: "B · 02",
    intent: "Thinking about it, not committed",
    cards: [
      {
        id: "id1",
        title: "Cost Centre — emergency-comms product scoping",
        summary:
          "Closes out the emergency-communications side of the portfolio. Needs a proper first-scope conversation before it gets real.",
        app: "CC",
        ref: "idea",
        timestamp: "seed",
        pinned: false,
      },
      {
        id: "id2",
        title: "Auto-generated monthly wrap-ups",
        summary:
          "MAP could produce a printable monthly summary at month-end from what shipped — no manual write-up needed.",
        app: "MP",
        ref: "idea",
        timestamp: "seed",
        pinned: false,
      },
      {
        id: "id3",
        title: "Bird's-eye dashboard once several products are live",
        summary:
          "When multiple products are live at once, one view showing active users, uptime, and deploy frequency across the whole portfolio.",
        app: "MP",
        ref: "idea",
        timestamp: "seed",
        pinned: false,
      },
    ],
  },
  {
    id: "clusters",
    title: "How the pieces fit together",
    coord: "C · 01",
    intent: "Cross-product groupings",
    clusters: [
      {
        id: "c-foundations",
        title: "The shared foundations everything runs on",
        summary:
          "Cloud, shared customer and asset data, and a single login sit behind every product. Buy one product; the shared context comes for free.",
        children: [
          { app: "NB", label: "Live and healthy" },
          { app: "EI", label: "Standing up next" },
          { app: "EC", label: "Standing up soon after" },
          { app: "CC", label: "Standing up later" },
        ],
      },
      {
        id: "c-hw",
        title: "Hardware in the field feeding live cloud data",
        summary:
          "TOCBox is our physical presence on the elevator — power and connectivity for anything else we put up there. Elevator IoT and Cost Centre both lean on it.",
        children: [
          { app: "TB", label: "The physical box in the field" },
          { app: "EI", label: "Elevator-operation telemetry" },
          { app: "CC", label: "Emergency comms (planned)" },
        ],
      },
      {
        id: "c-comms",
        title: "The emergency-communications family",
        summary:
          "Cost Centre closes out this side of the portfolio. Testing and telephony infrastructure sit inside or alongside.",
        children: [
          { app: "CC", label: "The product itself" },
          { app: "TC", label: "Test-call harness" },
          { app: "SS", label: "Telephony (SIP) server" },
        ],
      },
    ],
  },
];

/* App-badge palette hint (letter -> tag colour class) */
const APP_CLASS = {
  NB: "primary",
  TB: "accent",
  EI: "accent",
  EC: "primary",
  CC: "accent",
  MP: "",
  TC: "",
  SS: "",
};

/* ─────────────────────────────────────────────────────────────
 * Reference-toggle bar (small, subtle — Owner chose the mix,
 * this exists only so they can compare against the pure sources)
 * ────────────────────────────────────────────────────────── */

function ReferenceToggle({ direction, onChange, presenting, onPresent }) {
  return (
    <div className="map-direction-picker" data-testid="map-direction-picker">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 h-14 flex items-center justify-between gap-4">
        <div className="flex items-center gap-3 min-w-0">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm opacity-80 hover:opacity-100"
            data-testid="map-back-home"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="hidden sm:inline">Home</span>
          </Link>
          <span className="opacity-40 hidden sm:inline">·</span>
          <span className="map-mono text-xs opacity-70 whitespace-nowrap hidden md:inline">
            Preview · pick a look
          </span>
        </div>

        <ToggleGroup
          type="single"
          value={direction}
          onValueChange={(v) => v && onChange(v)}
          data-testid="map-direction-toggle"
        >
          <ToggleGroupItem value="mix" className="text-xs sm:text-sm px-3" data-testid="map-direction-mix">
            MAP
          </ToggleGroupItem>
          <ToggleGroupItem value="2" className="text-xs sm:text-sm px-3" data-testid="map-direction-2">
            <span className="opacity-60">alt · </span>Editorial
          </ToggleGroupItem>
          <ToggleGroupItem value="3" className="text-xs sm:text-sm px-3" data-testid="map-direction-3">
            <span className="opacity-60">alt · </span>Blueprint
          </ToggleGroupItem>
        </ToggleGroup>

        <div className="hidden md:flex items-center gap-2">
          <Button
            size="sm"
            variant={presenting ? "default" : "outline"}
            onClick={onPresent}
            data-testid="map-present-toggle"
          >
            <Presentation className="w-4 h-4 mr-2" />
            {presenting ? "Presenting" : "Present"}
          </Button>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
 * Shared bits used by every renderer
 * ────────────────────────────────────────────────────────── */

function AppTag({ app }) {
  const cls = APP_CLASS[app] || "";
  return (
    <span className={`map-app-tag ${cls}`} data-testid={`map-app-tag-${app.toLowerCase()}`}>
      {app}
    </span>
  );
}

function CardMeta({ card, showRef = false }) {
  return (
    <div className="map-card-footer">
      <div className="flex items-center gap-2">
        <AppTag app={card.app} />
        {showRef && card.ref && (
          <span className="map-mono opacity-60">{card.ref}</span>
        )}
      </div>
      <span className="map-timestamp inline-flex items-center gap-1">
        <Clock className="w-3 h-3 opacity-70" />
        {card.timestamp}
      </span>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
 * PRIMARY — MAP mix direction (Owner-chosen D2 + D3 merge)
 * ────────────────────────────────────────────────────────── */

function DirectionMix({ presenting, onOpenCard }) {
  return (
    <div className="map-scope" data-present={presenting ? "true" : "false"} data-testid="map-direction-mix-render">
      <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-10 pt-8">
        <div className="map-title-block" data-testid="map-title-block">
          <div>
            <div className="map-title-block-word">MAP</div>
            <div className="map-title-block-sub">
              Main Access Panel · September 2026 · Revision 01
            </div>
          </div>
          <div className="flex items-center gap-4 flex-wrap justify-end">
            <span className="map-title-block-tagline">
              Where we are, where we're headed, and what's next.
            </span>
            <div className="flex items-center gap-2">
              <span className="map-mono text-xs opacity-70" data-testid="map-last-refreshed-label">
                Fresh · 4m ago
              </span>
              <Button size="sm" variant="outline" data-testid="map-refresh-now-button">
                <RefreshCw className="w-4 h-4 mr-2" /> Refresh
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-10 pb-16">
        {MOCK_LANES.map((lane) => (
          <section className="map-lane" key={lane.id} data-testid={`map-lane-${lane.id}`}>
            <div className="map-lane-header">
              <div>
                <span className="map-lane-coord">{lane.coord}</span>
                <div className="map-lane-title mt-2">{lane.title}</div>
                <div className="map-lane-intent">{lane.intent}</div>
              </div>
              <span className="map-mono text-xs opacity-60">
                {lane.cards ? `${lane.cards.length} items` : `${lane.clusters.length} groups`}
              </span>
            </div>

            {lane.cards && (
              <div>
                <div className="grid gap-4 lg:grid-cols-3 mb-4">
                  {lane.cards.slice(0, 2).map((card, idx) => (
                    <div
                      key={card.id}
                      className={`map-hero-card ${idx === 1 ? "warm" : ""}`}
                      onClick={() => onOpenCard(card)}
                      data-testid={`map-card-${card.id}`}
                      style={{ gridColumn: idx === 0 ? "span 2" : "span 1" }}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <h3 className="map-hero-title">{card.title}</h3>
                        {card.pinned && (
                          <Pin
                            className="w-4 h-4 shrink-0"
                            style={{ color: "hsl(var(--map-primary))" }}
                            data-testid={`map-card-${card.id}-pinned`}
                          />
                        )}
                      </div>
                      <p className="map-hero-summary">{card.summary}</p>
                      <div className="mt-5 flex items-center justify-between gap-3">
                        <AppTag app={card.app} />
                        <span className="map-timestamp inline-flex items-center gap-1.5">
                          <Clock className="w-3.5 h-3.5 opacity-70" />
                          {card.timestamp}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>

                {lane.cards.length > 2 && (
                  <div>
                    {lane.cards.slice(2).map((card) => (
                      <div
                        key={card.id}
                        className="map-compact-card"
                        onClick={() => onOpenCard(card)}
                        data-testid={`map-card-${card.id}`}
                      >
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap mb-1.5">
                            <AppTag app={card.app} />
                          </div>
                          <div className="map-compact-title">{card.title}</div>
                          <div className="map-compact-summary">{card.summary}</div>
                        </div>
                        <span className="map-timestamp shrink-0 hidden md:inline">{card.timestamp}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {lane.clusters && (
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {lane.clusters.map((cluster) => (
                  <div className="map-cluster-card" key={cluster.id} data-testid={`map-cluster-${cluster.id}`}>
                    <h3 className="map-cluster-title">{cluster.title}</h3>
                    <p className="map-cluster-summary">{cluster.summary}</p>
                    <div className="mt-4">
                      {cluster.children.map((child, idx) => (
                        <div key={idx} className="map-cluster-child">
                          <AppTag app={child.app} />
                          <span className="opacity-85">{child.label}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
        ))}
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
 * ALT · Editorial (pure D2) — kept for reference comparison
 * ────────────────────────────────────────────────────────── */

function DirectionEditorial({ presenting, onOpenCard }) {
  return (
    <div className="map-scope" data-present={presenting ? "true" : "false"} data-testid="map-direction-2-render">
      <div className="map-hero-strip">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-10">
          <div className="flex items-baseline justify-between gap-4 flex-wrap">
            <div>
              <div className="map-mono text-xs opacity-60 tracking-widest uppercase mb-2">September · 2026</div>
              <h1 className="map-hero-title">MAP</h1>
              <p className="opacity-70 text-lg mt-2 max-w-xl">
                Where we are, where we're headed, and what's next.
              </p>
            </div>
            <div className="flex items-center gap-3">
              <span className="map-mono text-xs opacity-60" data-testid="map-last-refreshed-label">
                Fresh · 4m ago
              </span>
              <Button size="sm" variant="outline" data-testid="map-refresh-now-button">
                <RefreshCw className="w-4 h-4 mr-2" /> Refresh
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-10">
        {MOCK_LANES.map((lane) => (
          <section className="map-lane" key={lane.id} data-testid={`map-lane-${lane.id}`}>
            <div className="map-lane-header">
              <div>
                <div className="map-lane-title">{lane.title}</div>
                <div className="opacity-60 text-sm mt-1">{lane.intent}</div>
              </div>
              <span className="map-mono text-xs opacity-60">
                {lane.cards ? `${lane.cards.length} items` : `${lane.clusters.length} groups`}
              </span>
            </div>

            {lane.cards && (
              <div className="grid gap-4 lg:grid-cols-3">
                {lane.cards.slice(0, 2).map((card, idx) => (
                  <div
                    key={card.id}
                    className={`map-hero-card ${idx === 1 ? "warm" : ""}`}
                    onClick={() => onOpenCard(card)}
                    data-testid={`map-card-${card.id}`}
                    style={{ gridColumn: idx === 0 ? "span 2" : "span 1" }}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <h3 className="map-heading text-xl font-semibold leading-tight">{card.title}</h3>
                      {card.pinned && <Pin className="w-4 h-4 shrink-0" style={{ color: "hsl(var(--map-primary))" }} />}
                    </div>
                    <p className="mt-3 opacity-85 leading-relaxed">{card.summary}</p>
                    <div className="mt-5 flex items-center justify-between gap-3">
                      <AppTag app={card.app} />
                      <span className="map-timestamp inline-flex items-center gap-1.5">
                        <Clock className="w-3.5 h-3.5 opacity-70" />
                        {card.timestamp}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {lane.cards && lane.cards.length > 2 && (
              <div className="mt-4">
                {lane.cards.slice(2).map((card) => (
                  <div
                    key={card.id}
                    className="map-compact-card"
                    onClick={() => onOpenCard(card)}
                    data-testid={`map-card-${card.id}`}
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <AppTag app={card.app} />
                      </div>
                      <div className="font-medium">{card.title}</div>
                      <div className="opacity-70 text-sm mt-1">{card.summary}</div>
                    </div>
                    <span className="map-timestamp shrink-0 hidden md:inline">{card.timestamp}</span>
                  </div>
                ))}
              </div>
            )}

            {lane.clusters && (
              <div className="grid gap-4 md:grid-cols-2">
                {lane.clusters.map((cluster) => (
                  <div className="map-hero-card" key={cluster.id} data-testid={`map-cluster-${cluster.id}`}>
                    <h3 className="map-heading text-lg font-semibold">{cluster.title}</h3>
                    <p className="opacity-70 text-sm mt-2 leading-relaxed">{cluster.summary}</p>
                    <div className="mt-4 space-y-2">
                      {cluster.children.map((child, idx) => (
                        <div key={idx} className="flex items-center gap-3 text-sm">
                          <AppTag app={child.app} />
                          <span className="opacity-85">{child.label}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
        ))}
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
 * ALT · Blueprint (pure D3) — kept for reference comparison
 * ────────────────────────────────────────────────────────── */

function DirectionBlueprint({ presenting, onOpenCard }) {
  return (
    <div className="map-scope" data-present={presenting ? "true" : "false"} data-testid="map-direction-3-render">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-6">
        <div className="map-title-block mb-6" data-testid="map-title-block">
          <div className="flex items-baseline gap-4 flex-wrap">
            <span className="map-heading text-2xl font-bold">MAP</span>
            <span className="map-dimension-marker">Main Access Panel · Rev 001 · Sept 2026</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="map-mono text-xs opacity-70" data-testid="map-last-refreshed-label">
              Fresh · 4m ago
            </span>
            <Button size="sm" variant="outline" data-testid="map-refresh-now-button">
              <RefreshCw className="w-4 h-4 mr-2" /> Refresh
            </Button>
          </div>
        </div>

        <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3">
          {MOCK_LANES.map((lane) => (
            <div className="map-lane" key={lane.id} data-testid={`map-lane-${lane.id}`}>
              <div className="map-lane-header">
                <div>
                  <div className="map-lane-title">{lane.title}</div>
                  <div className="map-dimension-marker mt-1">{lane.intent}</div>
                </div>
                <span className="map-lane-coord">
                  {lane.coord} · {lane.cards ? `n=${lane.cards.length}` : `n=${lane.clusters.length}`}
                </span>
              </div>
              <div className="p-3 space-y-3">
                {lane.cards &&
                  lane.cards.map((card) => (
                    <div
                      key={card.id}
                      className={`map-card ${card.pinned ? "pinned" : ""}`}
                      onClick={() => onOpenCard(card)}
                      data-testid={`map-card-${card.id}`}
                    >
                      <div className="map-card-header">
                        <div className="map-card-title">{card.title}</div>
                        {card.pinned && (
                          <Pin
                            className="w-3.5 h-3.5 shrink-0"
                            style={{ color: "hsl(var(--map-primary))" }}
                          />
                        )}
                      </div>
                      <div className="map-card-summary">{card.summary}</div>
                      <CardMeta card={card} />
                    </div>
                  ))}
                {lane.clusters &&
                  lane.clusters.map((cluster) => (
                    <div key={cluster.id} className="map-card" data-testid={`map-cluster-${cluster.id}`}>
                      <div className="map-card-header">
                        <div className="map-card-title">{cluster.title}</div>
                      </div>
                      <div className="map-card-summary mb-3">{cluster.summary}</div>
                      <div className="space-y-1.5 pl-4 border-l" style={{ borderColor: "hsl(var(--map-border))" }}>
                        {cluster.children.map((child, idx) => (
                          <div
                            key={idx}
                            className="map-card cluster-child flex items-center gap-2 text-xs py-1 px-2"
                          >
                            <AppTag app={child.app} />
                            <span className="opacity-85">{child.label}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
 * Main page — routes direction picker to the correct renderer
 * ────────────────────────────────────────────────────────── */

export default function MapPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialDirection = searchParams.get("d") || "mix";
  const [direction, setDirection] = useState(initialDirection);
  const [presenting, setPresenting] = useState(false);
  const [openCard, setOpenCard] = useState(null);

  useEffect(() => {
    setSearchParams({ d: direction }, { replace: true });
  }, [direction, setSearchParams]);

  const themeAttr = useMemo(() => {
    if (direction === "2") return "map-editorial-light";
    if (direction === "3") return "map-blueprint-light";
    return "map-mix-light";
  }, [direction]);

  return (
    <div data-theme={themeAttr} data-testid="map-theme-wrapper" style={{ minHeight: "100vh" }}>
      <ReferenceToggle
        direction={direction}
        onChange={setDirection}
        presenting={presenting}
        onPresent={() => setPresenting((v) => !v)}
      />
      {direction === "mix" && <DirectionMix presenting={presenting} onOpenCard={setOpenCard} />}
      {direction === "2" && <DirectionEditorial presenting={presenting} onOpenCard={setOpenCard} />}
      {direction === "3" && <DirectionBlueprint presenting={presenting} onOpenCard={setOpenCard} />}

      <Sheet open={!!openCard} onOpenChange={(v) => !v && setOpenCard(null)}>
        <SheetContent side="right" className="w-full sm:max-w-[520px]" data-testid="map-detail-drawer">
          {openCard && (
            <>
              <SheetHeader>
                <div className="flex items-center gap-2 mb-2">
                  <AppTag app={openCard.app} />
                  <span className="map-mono text-xs opacity-60">{openCard.ref}</span>
                </div>
                <SheetTitle className="text-left leading-snug">{openCard.title}</SheetTitle>
                <SheetDescription className="text-left leading-relaxed">
                  {openCard.summary}
                </SheetDescription>
              </SheetHeader>

              <div className="mt-6 space-y-4 text-sm">
                <div
                  className="p-3 rounded border"
                  style={{
                    background: "hsl(var(--map-muted) / 0.4)",
                    borderColor: "hsl(var(--map-border))",
                  }}
                >
                  <div className="map-mono text-xs opacity-70 uppercase tracking-widest mb-1">Source</div>
                  <div className="flex items-center gap-2">
                    <ExternalLink className="w-3.5 h-3.5" />
                    <span className="opacity-90">Behind-the-scenes reference: {openCard.ref}</span>
                  </div>
                </div>

                <div className="map-edit-only">
                  <div className="map-mono text-xs opacity-70 uppercase tracking-widest mb-2">Talk track</div>
                  <div
                    className="p-3 rounded border text-sm opacity-70 italic"
                    style={{
                      background: "hsl(var(--map-muted) / 0.25)",
                      borderColor: "hsl(var(--map-border))",
                    }}
                  >
                    (Add a short talk-track note here so you know what to say when presenting this to the team.)
                  </div>
                </div>

                <div className="map-edit-only flex flex-wrap gap-2 pt-2">
                  <Button size="sm" variant="outline" data-testid="map-card-pin-button">
                    <Pin className="w-3.5 h-3.5 mr-2" />
                    {openCard.pinned ? "Unpin" : "Pin"}
                  </Button>
                  <Button size="sm" variant="outline" data-testid="map-card-hide-toggle">
                    <EyeOff className="w-3.5 h-3.5 mr-2" />
                    Hide in present
                  </Button>
                  <Button size="sm" variant="outline" data-testid="map-card-add-note">
                    <MessageSquare className="w-3.5 h-3.5 mr-2" />
                    Add note
                  </Button>
                </div>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
